import { useEffect, useState } from "react";

import { useFlotiqStorageConfig } from "@locators/components/shared/hooks/useFlotiqStorageConfig";
import { getTransformedMarkers } from "@locators/components/shared/utils";

import type { FlotiqStore } from "@jti/fe-common";
import { FlotiqApi } from "@jti/rest-api";

interface UseNearbyStoresParams {
  store?: FlotiqStore;
  isEnabled?: boolean;
  radiusMeters?: number;
  resultLimit?: number;
}

const defaultRadiusMeters = 1000000;
const defaultResultLimit = 100;
const STORE_COUNT_TO_SHOW = 10;
const SORT_BY_GEO_DISTANCE = "_geo_distance";

export const useNearbyStores = (params: UseNearbyStoresParams) => {
  const { store, isEnabled = true, radiusMeters = defaultRadiusMeters, resultLimit = defaultResultLimit } = params;
  const { flotiqConfig } = useFlotiqStorageConfig();
  const [nearbyStores, setNearbyStores] = useState<FlotiqStore[]>([]);

  useEffect(() => {
    const fetchNearby = async () => {
      if (!isEnabled || !store?.Location?.lat || !store?.Location?.lon) {
        setNearbyStores([]);
        return;
      }

      try {
        const api = FlotiqApi.init(flotiqConfig?.flotiqApiKey, flotiqConfig?.contentType);

        const response = await api.getStores({
          radius: radiusMeters,
          lat: store.Location.lat,
          lng: store.Location.lon,
          storesLimit: resultLimit,
          orderBy: SORT_BY_GEO_DISTANCE,
        });

        if (!response?.data?.length) {
          setNearbyStores([]);
          return;
        }

        const allExceptCurrentOne = getTransformedMarkers(response.data).filter((item) => item.id !== store.id);
        setNearbyStores(allExceptCurrentOne.slice(0, STORE_COUNT_TO_SHOW));
      } catch (error) {
        console.error(error);
      }
    };

    fetchNearby();
  }, [isEnabled, radiusMeters, resultLimit, store?.id, store?.Location?.lat, store?.Location?.lon]);

  return { nearbyStores };
};
